import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { List, Set } from 'immutable';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { DialogActionsEnum, IDialogTrigger } from '../../../dialog/dialog.data';
import { DialogService } from '../../../dialog/dialog.service';
import {
  SEARCH_LIST_FILTER,
  SortByEnum,
  SortOrderEnum,
} from '../../../entity-data/entity-data.data';
import { INoItemsOptions } from '../../../feed/feed.interface';
import { Item } from '../../../item-data/item.model';
import { SearchActionTriggersEnum } from '../../../search/search.component';
import {
  I2gNgTrackingActionBuilder,
  ITrackingProperties,
} from 'i2g-ng-tracking';
import { TrackingAdditionalPropertiesBuilder } from '../../../tracking/tracking-additional-properties.builder';
import { TrackingObjectBuilder } from '../../../tracking/tracking-object.builder';
import {
  IItemsMultipleSelectAdditionalProperties,
  ITrackingAction,
  ITrackingObject,
  TrackingDialogNamesEnum,
  TrackingScreenNamesEnum,
} from '../../../tracking/tracking.data';
import { ItemDataStore } from '../../../item-data/item-data.store';
import { ItemDataDispatcher } from '../../../item-data/item-data.dispatcher';
import { filter, map, take, takeUntil, tap } from 'rxjs/operators';

export const DIALOG_TYPE = 'item-picker';

@Component({
  selector: 'app-item-picker-dialog',
  templateUrl: './item-picker-dialog.component.html',
  styleUrls: ['./item-picker-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemPickerDialogComponent implements OnInit, OnDestroy {
  @Input() maxItemsAllowed: number;
  @Input() itemsAdded: number;
  @Input() displayItemType: boolean;
  @Input() showProductCode: boolean;
  @Input() productNameEnabled: boolean;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  @Output() onSelectItems: EventEmitter<List<Item>> = new EventEmitter<
    List<Item>
  >();

  private _inSearchMode: boolean;
  private _selectedItems: Set<Item>;
  private _searchActionTriggers: Subject<SearchActionTriggersEnum>;
  private _bulkActionControls: { [itemId: string]: FormControl } = {};
  private _destroyed: Subject<boolean> = new Subject<boolean>();

  dialogType: string;
  noItemsOptions: INoItemsOptions;
  trackingObject: ITrackingObject;
  addBulkAction: ITrackingAction;
  filteredItems$: Observable<List<Item>>;
  numberOfSelectedItems$: Observable<number>;
  searchActionTriggers$: Observable<SearchActionTriggersEnum>;
  error$: BehaviorSubject<string>;
  dialogButtonText$: BehaviorSubject<string>;
  itemsMultipleSelectTrackingProperties$: BehaviorSubject<IItemsMultipleSelectAdditionalProperties>;

  constructor(
    private _translateService: TranslateService,
    private _dialogService: DialogService,
    private _itemDataDispatcher: ItemDataDispatcher,
    private _itemDataStore: ItemDataStore,
    private _trackingObjectBuilder: TrackingObjectBuilder,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _trackingAdditionalPropertiesBuilder: TrackingAdditionalPropertiesBuilder
  ) {}

  ngOnInit() {
    this._searchActionTriggers = new Subject<SearchActionTriggersEnum>();

    this.trackingObject = this._trackingObjectBuilder.buildDialog(
      TrackingDialogNamesEnum.items_multiple_select
    );
    this.addBulkAction = this._trackingActionBuilder.buildButtonTapped(
      'add_bulk',
      this.trackingScreenName
    );
    this.dialogType = DIALOG_TYPE;
    this.noItemsOptions = {
      description: this._translateService.instant('LABEL.NO_ITEMS'),
    };

    this.searchActionTriggers$ = this._searchActionTriggers.asObservable();
    this.error$ = new BehaviorSubject<string>(null);
    this.dialogButtonText$ = new BehaviorSubject<string>(
      this._translateService.instant('ACTION.ADD_ITEM')
    );
    this.itemsMultipleSelectTrackingProperties$ = new BehaviorSubject<IItemsMultipleSelectAdditionalProperties>(
      null
    );

    // feed results
    this.filteredItems$ = this._itemDataStore.getSortedList(SEARCH_LIST_FILTER);

    // update number of selected items
    this.numberOfSelectedItems$ = this._itemDataStore
      .getSelectedEntities()
      .pipe(
        tap((items: Set<Item>) => {
          this._selectedItems = items || Set([]);
        }),
        map((items: Set<Item>) => (items && items.size) || 0)
      );

    // on open dialog
    this._dialogService.dialogTriggers$
      .pipe(
        filter((dialogTrigger: IDialogTrigger) => {
          return (
            dialogTrigger.type === this.dialogType &&
            dialogTrigger.action === DialogActionsEnum.open
          );
        }),
        takeUntil(this._destroyed)
      )
      .subscribe(() => {
        this._inSearchMode = false;

        this.resetSelectedItems();

        this._itemDataStore
          .getListFetched(SEARCH_LIST_FILTER)
          .pipe(
            takeUntil(this._destroyed),
            take(1),
            filter((fetched: boolean) => !fetched)
          )
          .subscribe((fetched: boolean) => this.searchItems());
      });
  }

  onSearch(searchTerm: string) {
    this.resetSelectedItems();

    if (searchTerm) {
      this._inSearchMode = true;

      this.searchItems(searchTerm.toLowerCase());
    } else {
      this._inSearchMode = false;

      this.searchItems();
    }
  }

  searchItems(query?: string) {
    this._itemDataDispatcher.searchList(
      query,
      SortByEnum.description,
      SortOrderEnum.asc,
      false,
      false,
      false
    );
  }

  onClickItem(item: Item) {
    const currentValue = this._bulkActionControls[item.id].value;

    this._bulkActionControls[item.id].setValue(!currentValue, {
      emitEvent: true,
    });
  }

  getBulkActionControl(item: Item): FormControl {
    const itemId = item.id;
    const isSelected = this._selectedItems.find(
      (selectedItem: Item) => selectedItem.id === itemId
    );

    if (!this._bulkActionControls) {
      this._bulkActionControls = {};
    }

    if (!this._bulkActionControls[itemId]) {
      this._bulkActionControls[itemId] = new FormControl(isSelected);

      this._bulkActionControls[itemId].valueChanges
        .pipe(takeUntil(this._destroyed))
        .subscribe((value: boolean) => this.onBulkActionSelect(item, value));
    } else {
      this._bulkActionControls[itemId].setValue(isSelected, {
        emitEvent: false,
      });
    }

    return this._bulkActionControls[itemId];
  }

  onBulkActionSelect(item: Item, selected: boolean) {
    const belowLimit =
      this._selectedItems.size < this.maxItemsAllowed - this.itemsAdded;

    this.error$.next(null);

    if (selected) {
      if (belowLimit) {
        this._itemDataDispatcher.addToSelectedList(item);
      } else {
        this.error$.next(
          this._translateService.instant('WARNING.BULK_SELECT_LIMIT', {
            maxNumber: this.maxItemsAllowed,
          })
        );

        this._itemDataDispatcher.removeFromSelectedList(item);

        this._bulkActionControls[item.id].setValue(false, { emitEvent: false });
      }
    } else {
      this._itemDataDispatcher.removeFromSelectedList(item);
    }

    const numberOfSelectedItems = this._selectedItems.size;

    if (numberOfSelectedItems <= 1) {
      this.dialogButtonText$.next(
        this._translateService.instant('ACTION.ADD_ITEM')
      );
    } else {
      this.dialogButtonText$.next(
        this._translateService.instant('ACTION.ADD_N_ITEMS', {
          count: numberOfSelectedItems,
        })
      );
    }

    // update tracking addtional properties
    this.itemsMultipleSelectTrackingProperties$.next(
      this._trackingAdditionalPropertiesBuilder.buildItemMultipleSelectOptions(
        numberOfSelectedItems
      )
    );
  }

  saveSelectedItems() {
    this.onSelectItems.emit(this._selectedItems.toList());

    this.resetSelectedItems();
  }

  resetSelectedItems() {
    this.error$.next(null);

    this.dialogButtonText$.next(
      this._translateService.instant('ACTION.ADD_ITEM')
    );

    this._itemDataDispatcher.resetSelectedList();
  }

  onClose() {
    if (this._inSearchMode) {
      this._searchActionTriggers.next(SearchActionTriggersEnum.reset);
    }
  }

  trackByFn = (index: number, item: Item) => item.id;

  ngOnDestroy() {
    this._itemDataDispatcher.clearList(SEARCH_LIST_FILTER);

    this._destroyed.next(true);
    this._destroyed.unsubscribe();
  }
}
